import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, MapPin, Home as HomeIcon, Heart, PawPrint, ChevronDown } from "lucide-react";
import { Reveal, MaskedLine, EASE } from "@/components/site/Motion";
import ValueMarquee from "@/components/site/ValueMarquee";
import Testimonials from "@/components/site/Testimonials";
import Eden from "@/components/site/Eden";
import ZoneMap from "@/components/site/ZoneMap";

const IMG = {
  hero: "/photos/bueno-enfants.webp",
  child: "/photos/bueno-plage.webp",
  training: "/photos/bueno-collier.webp",
  family: "/photos/bueno-jardin.webp",
};

const CHAPTERS = [
  {
    num: "01",
    title: "La médiation animale",
    text: "L'animal devient un pont entre les membres de la famille. Sa présence apaise, ouvre la parole et permet à chacun — enfants comme parents — de renouer le dialogue autrement. Formée à l'Institut français de zoothérapie en 2019. Selon votre situation, une équithérapie est également possible avec mon cheval Eden.",
    img: IMG.child,
    pos: "50% 45%",
    aspect: "aspect-[4/5]",
    alt: "Enfant souriante et Bueno complices au bord de la mer",
    blob: "blob-1",
    examples: [
      "Apaiser un enfant anxieux, en colère ou renfermé",
      "Rouvrir le dialogue après une séparation ou un deuil",
      "Renforcer la confiance en soi grâce à l'animal",
      "Recréer un vrai moment de partage en famille",
    ],
  },
  {
    num: "02",
    title: "L'accompagnement à la parentalité",
    text: "Un espace d'écoute sans jugement pour traverser les tensions du quotidien, poser un cadre bienveillant et retrouver confiance dans votre rôle de parent. Forte de 3 ans d'expérience comme éducatrice spécialisée en protection de l'enfance, cela renforce ma volonté et ma capacité à accompagner votre famille — enfants et parents.",
    img: IMG.family,
    pos: "50% 60%",
    aspect: "aspect-[5/4]",
    alt: "Enfants jouant avec Bueno dans le jardin",
    blob: "blob-2",
    examples: [
      "Crises, colères et oppositions du quotidien",
      "Poser un cadre bienveillant, sans crier",
      "Épuisement parental et charge mentale",
      "Écrans, devoirs, routines du soir",
    ],
  },
  {
    num: "03",
    title: "L'éducation canine",
    text: "Un chien apaisé, c'est un foyer apaisé. Des méthodes positives et respectueuses pour que votre compagnon trouve sa place dans l'équilibre familial. Titulaire de l'ACACED chien, chat, NAC depuis août 2026 — le diplôme obligatoire pour exercer.",
    img: IMG.training,
    pos: "50% 38%",
    aspect: "aspect-[4/5]",
    alt: "Bueno et son collier, photographié de dos",
    blob: "blob-3",
    examples: [
      "Propreté et premiers apprentissages du chiot",
      "Tirer en laisse, rappel, aboiements",
      "Arrivée d'un bébé ou d'un nouvel animal à la maison",
      "Cohabitation chien-chat et bonnes manières",
    ],
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const [openChapter, setOpenChapter] = useState(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden px-6 pt-32 sm:px-10 md:pt-40 lg:px-16" data-testid="home-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              className="overline-label mb-6"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
            >
              Coaching familial à l'aide de l'animal
            </motion.p>
            <h1 className="font-serif text-5xl font-medium leading-[1.04] tracking-tight text-ink sm:text-6xl md:text-7xl">
              <MaskedLine delay={0.15}>Recréer du lien,</MaskedLine>
              <MaskedLine delay={0.29}>retrouver l'équilibre,</MaskedLine>
              <MaskedLine delay={0.43} className="italic text-terracotta">grâce à l'animal.</MaskedLine>
            </h1>
            <motion.p
              className="mt-8 max-w-xl text-base leading-relaxed text-ink/70 md:text-lg"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
              data-testid="home-hero-intro"
            >
              Une méthode unique alliant médiation animale, accompagnement à la
              parentalité et éducation canine. Je me déplace à votre domicile,
              là où se jouent vraiment les problématiques à régler.
            </motion.p>
            <motion.div
              className="mt-10 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            >
              <Link to="/methode" className="btn-primary" data-testid="home-hero-cta-methode">
                Découvrir la méthode <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn-ghost" data-testid="home-hero-cta-contact">
                Prendre contact
              </Link>
            </motion.div>
            <motion.div
              className="mt-12 flex items-center gap-8 text-xs font-semibold uppercase tracking-[0.2em] text-ink/45"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.2 }}
              data-testid="home-hero-badges"
            >
              <span className="flex items-center gap-2"><Heart size={14} className="text-terracotta" /> 3 disciplines</span>
              <span className="flex items-center gap-2"><HomeIcon size={14} className="text-terracotta" /> À domicile</span>
              <span className="hidden items-center gap-2 sm:flex"><MapPin size={14} className="text-terracotta" /> Coutances &amp; alentour</span>
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div
              className="absolute -left-6 -top-6 h-full w-full blob-2 bg-sand/40"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
              aria-hidden="true"
            />
            <motion.div
              className="blob-1 relative aspect-[4/5] overflow-hidden shadow-[0_40px_80px_-30px_rgba(43,58,48,0.45)]"
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.35, ease: EASE }}
            >
              <motion.img
                src={IMG.hero}
                alt="Trois enfants assis dans l'herbe avec Bueno, chien médiateur en formation"
                className="h-[115%] w-full object-cover"
                style={{ y: imgY, objectPosition: "50% 68%" }}
                data-testid="home-hero-image"
              />
            </motion.div>
            <motion.div
              className="absolute -bottom-6 -right-4 rounded-3xl border border-stone-border bg-cream/90 px-6 py-4 shadow-xl backdrop-blur-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
              data-testid="home-hero-caption"
            >
              <p className="font-serif text-lg italic text-ink">Bueno, médiateur en formation</p>
            </motion.div>
          </div>
        </div>
        <div className="h-24 md:h-32" />
      </section>

      <ValueMarquee />

      {/* MANIFESTO CHAPTERS */}
      <section className="px-6 py-24 sm:px-10 md:py-32 lg:px-16" data-testid="home-chapters">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="overline-label">Le manifeste</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium leading-[1.15] tracking-tight text-ink sm:text-4xl md:text-5xl">
              Trois disciplines, un seul fil : <span className="italic text-terracotta">le lien.</span>
            </h2>
          </Reveal>

          <div className="mt-20 space-y-24 md:space-y-32">
            {CHAPTERS.map((ch, i) => (
              <div
                key={ch.num}
                className={`grid items-center gap-10 md:grid-cols-12 ${i % 2 === 1 ? "" : ""}`}
                data-testid={`home-chapter-${ch.num}`}
              >
                <Reveal className={`md:col-span-5 ${i % 2 === 1 ? "md:order-2" : ""}`} y={50}>
                  <div className="relative">
                    <div className={`absolute -right-5 -top-5 h-full w-full ${ch.blob} bg-terracotta-soft`} aria-hidden="true" />
                    <div className={`${ch.blob} relative ${ch.aspect} overflow-hidden shadow-[0_30px_60px_-30px_rgba(43,58,48,0.4)]`}>
                      <img
                        src={ch.img}
                        alt={ch.alt}
                        loading="lazy"
                        style={{ objectPosition: ch.pos }}
                        className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                      />
                    </div>
                  </div>
                </Reveal>
                <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-1 md:pr-8" : "md:col-start-7"}`}>
                  <Reveal delay={0.1}>
                    <span className="font-serif text-7xl font-medium italic leading-none text-terracotta/25 md:text-8xl">
                      {ch.num}
                    </span>
                    <h3 className="mt-4 font-serif text-2xl font-medium text-ink sm:text-3xl">{ch.title}</h3>
                    <p className="mt-5 max-w-md text-base leading-relaxed text-ink/70">{ch.text}</p>
                    <button
                      type="button"
                      onClick={() => setOpenChapter(openChapter === ch.num ? null : ch.num)}
                      className="mt-7 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-terracotta transition-[gap] duration-300 hover:gap-3.5"
                      data-testid={`home-chapter-more-${ch.num}`}
                    >
                      {openChapter === ch.num ? "Refermer" : "Quelques exemples"}
                      <ChevronDown
                        size={15}
                        className={`transition-transform duration-300 ${openChapter === ch.num ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {openChapter === ch.num && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.45, ease: EASE }}
                          className="overflow-hidden"
                          data-testid={`home-chapter-examples-${ch.num}`}
                        >
                          <ul className="mt-6 space-y-2.5 border-l-2 border-terracotta/30 pl-5">
                            {ch.examples.map((ex) => (
                              <li key={ex} className="text-sm leading-relaxed text-ink/70">
                                {ex}
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Reveal>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Eden />

      {/* ZONE D'INTERVENTION */}
      <section className="bg-linen px-6 py-24 sm:px-10 md:py-32 lg:px-16" data-testid="home-zone">
        <div className="mx-auto grid max-w-7xl items-start gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline-label">Zone d'intervention</p>
              <h2 className="mt-4 font-serif text-3xl font-medium leading-[1.15] tracking-tight text-ink sm:text-4xl md:text-5xl">
                Je me déplace <span className="italic text-terracotta">chez vous.</span>
              </h2>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-ink/70 md:text-lg">
                Parce que c'est là que vivent les problématiques, c'est là que se
                construisent les solutions. Chaque accompagnement commence par une
                rencontre avec toute la famille — humains et animaux — dans votre
                environnement quotidien.
              </p>
            </Reveal>
            <div className="mt-10 space-y-5">
              {[
                { icon: HomeIcon, title: "À votre domicile", text: "Observation des interactions réelles, dans votre cadre de vie." },
                { icon: Heart, title: "Toute la famille", text: "Enfants, parents et animaux : chacun a sa place dans la méthode." },
              ].map((f, i) => (
                <Reveal key={f.title} delay={0.08 * i} y={30}>
                  <div
                    className="flex h-full gap-5 rounded-3xl border border-stone-border bg-cream p-6 transition-transform duration-300 hover:-translate-y-1"
                    data-testid={`home-zone-feature-${i + 1}`}
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-terracotta/10 text-terracotta">
                      <f.icon size={20} strokeWidth={1.8} />
                    </span>
                    <div>
                      <p className="font-serif text-xl font-medium text-ink">{f.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-ink/65">{f.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.05} y={30}>
              <div
                className="flex gap-5 rounded-3xl border border-stone-border bg-cream p-6 transition-transform duration-300 hover:-translate-y-1"
                data-testid="home-zone-feature-3"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-terracotta/10 text-terracotta">
                  <MapPin size={20} strokeWidth={1.8} />
                </span>
                <div>
                  <p className="font-serif text-xl font-medium text-ink">Coutances · Saint-Lô · Granville · Portbail</p>
                  <p className="mt-1 text-sm leading-relaxed text-ink/65">
                    Un rayon d'environ 30 km autour de Muneville-le-Bingard — au-delà, des frais kilométriques s'appliquent.
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.12} y={40}>
              <div className="mt-8 pb-4">
                <ZoneMap />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-forest-deep px-6 py-24 text-center sm:px-10 md:py-32" data-testid="home-cta">
        <PawPrint className="absolute -left-10 top-10 h-48 w-48 text-cream/5" strokeWidth={1} aria-hidden="true" />
        <PawPrint className="absolute -bottom-12 -right-8 h-56 w-56 rotate-45 text-cream/5" strokeWidth={1} aria-hidden="true" />
        <Reveal>
          <h2 className="mx-auto max-w-3xl font-serif text-3xl font-medium leading-[1.15] tracking-tight text-cream sm:text-4xl md:text-5xl">
            Prêt·e à écrire <span className="italic text-sand">une nouvelle histoire ?</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-cream/65">
            Le premier pas est un simple échange. Racontez-moi votre situation,
            nous verrons ensemble comment avancer.
          </p>
          <Link to="/contact" className="btn-primary mt-10" data-testid="home-cta-button">
            Laisser un message <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>

      <Testimonials />
    </div>
  );
}
